import React, { useState } from 'react'
import { BuildingOffice2Icon, CheckIcon, PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { listDepartments, activeDepartmentUrl } from '../services/departments'
import { switchToDepartment, addDepartment, removeDepartment } from '../services/departmentSwitch'
import { getPushDeviceToken } from '../services/native'

const label = (d) => d.name || d.url.replace(/^https?:\/\//, '')

/**
 * Switch between the departments remembered on this device (native app).
 * Switching keeps every department signed in; removing one signs this device
 * out of its notifications and forgets it.
 */
function DepartmentSwitcher({ onClose }) {
  const [departments, setDepartments] = useState(() => listDepartments())
  const [busy, setBusy] = useState(null)
  const active = activeDepartmentUrl()

  const remove = async (d) => {
    if (!window.confirm(`Remove ${label(d)} from this device? You'll need to connect and sign in again to use it.`)) return
    setBusy(d.url)
    await removeDepartment(d.url, getPushDeviceToken())
    setDepartments(listDepartments())
    setBusy(null)
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-slate-800 border border-slate-700 rounded-t-2xl sm:rounded-2xl p-4 pb-8 safe-bottom" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-white">Departments</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white" aria-label="Close">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-1">
          {departments.map(d => {
            const isActive = d.url === active
            return (
              <div key={d.url} className="flex items-center gap-2">
                <button
                  disabled={isActive || !!busy}
                  onClick={() => { setBusy(d.url); switchToDepartment(d.url) }}
                  className={`flex-1 flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors ${
                    isActive ? 'bg-amber-500/20 text-amber-500' : 'text-slate-200 hover:bg-slate-700/50'
                  }`}
                >
                  <BuildingOffice2Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1 font-medium truncate">{label(d)}</span>
                  {isActive && <CheckIcon className="w-5 h-5" />}
                  {busy === d.url && !isActive && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                </button>
                {!isActive && (
                  <button
                    disabled={!!busy}
                    onClick={() => remove(d)}
                    className="p-2 text-slate-500 hover:text-red-400"
                    aria-label={`Remove ${label(d)}`}
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
            )
          })}
        </div>

        <button
          disabled={!!busy}
          onClick={() => addDepartment()}
          className="mt-3 w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left text-slate-300 hover:bg-slate-700/50 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          <span className="font-medium">Add a department</span>
        </button>
        <p className="mt-2 px-3 text-xs text-slate-500">
          You stay signed in to each department and get notifications from all of them.
        </p>
      </div>
    </div>
  )
}

export default DepartmentSwitcher
